// @flow
import * as pubnubService from '../../services/pubnubService';
import { fetchBidLimitBalanceIfNeeded, getBidLimitBalance } from './bidLimit';
import { getUnixTime, parseISO } from 'date-fns';
import {
    LIVE_ASK_CHANGED,
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_LOADED,
    LIVE_AUCTION_MOVED,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_PAUSED,
    LIVE_AUCTION_RESUMED,
    LIVE_AUCTION_STARTED,
    LIVE_BID_ACCEPTED,
    LIVE_BID_RETRACTED,
    LIVE_LOT_CLOSED,
    LIVE_LOT_PASSED,
    LIVE_LOT_REOPENED,
    LIVE_LOT_SKIPPED,
    LIVE_LOT_SOLD,
    LIVE_LOT_UNSOLD,
    LIVE_MISSIVE,
    LIVE_NETWORK_DISCONNECTED,
    LIVE_NETWORK_RECONNECTED,
    LIVE_NEXT_LOT_LOADED,
    LIVE_NEXT_LOT_OPENED,
    LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
} from './actions';
import type { PubnubMessage, PubnubPresenceEvent, PubnubStatusEvent } from '../../types/pubnub';
import { fetchLiveCatalogRegistrationIfNeeded } from './catalogRegistration';
import { getBidderId } from './user';
import { getCatalog } from './catalog';
import { getLiveItemData } from './console';
import { getUiLiveBid } from './liveBid';

/* ACTION CREATORS */
export const createPubnubCatalogOnMessageReceived = (orgiginalCatalogId: number) => (message: PubnubMessage) => async (
    dispatch: Function,
    getState: Function
) => {
    const { message: eventJson } = message;

    let event;
    // When we receive events from the catalog channel, the message is a json string,
    if (typeof eventJson === 'string') {
        try {
            event = JSON.parse(eventJson);
        } catch (err) {
            event = {};
        }
    } else {
        // when we receive events from the lot channels, the message is an object
        event = eventJson;
        if (typeof event.Created === 'string') {
            const date = parseISO(event.Created);
            event.Created = getUnixTime(date);
        }
    }

    const { AuctionId: catalogId, EventType } = event;

    if (catalogId === orgiginalCatalogId) {
        const created = new Date().getTime(); // Why are we using this created and not the value in the event?
        const state = getState();
        const currentBidderId = getBidderId(state);
        const catalog = getCatalog(state, catalogId);
        const { currency } = catalog;

        if (EventType === 'ask-changed') {
            const { AskPrice, LotId, LotIndex, LotNumber } = event;
            return dispatch({
                payload: {
                    ask: AskPrice,
                    catalogId,
                    created,
                    currency,
                    itemId: LotId,
                    itemIndex: LotIndex,
                    lotNumber: LotNumber,
                },
                type: LIVE_ASK_CHANGED,
            });
        } else if (EventType === 'auction-closed') {
            return dispatch({ payload: { catalogId, created }, type: LIVE_AUCTION_ENDED });
        } else if (EventType === 'auction-loaded') {
            await dispatch(fetchLiveCatalogRegistrationIfNeeded(catalogId));

            return dispatch({
                payload: { catalogId, created },
                type: LIVE_AUCTION_LOADED,
            });
        } else if (EventType === 'auction-moved') {
            return dispatch({ payload: { catalogId, created }, type: LIVE_AUCTION_MOVED });
        } else if (EventType === 'auction-opened') {
            await dispatch(fetchLiveCatalogRegistrationIfNeeded(catalogId));

            return dispatch({
                payload: { catalogId, created },
                type: LIVE_AUCTION_OPENED,
            });
        } else if (EventType === 'auction-paused') {
            return dispatch({ payload: { catalogId, created }, type: LIVE_AUCTION_PAUSED });
        } else if (EventType === 'auction-resumed') {
            return dispatch({ payload: { catalogId, created }, type: LIVE_AUCTION_RESUMED });
        } else if (EventType === 'auction-started') {
            return dispatch({ payload: { catalogId, created }, type: LIVE_AUCTION_STARTED });
        } else if (EventType === 'bid-accepted') {
            const { Amount, AskPrice, AssignedId, BidSource, BidderId, LotId, LotIndex, LotNumber } = event;
            return dispatch(
                bidAccepted({
                    amount: Amount,
                    ask: AskPrice,
                    bidderId: BidderId,
                    catalogId,
                    created,
                    currency,
                    itemId: LotId,
                    itemIndex: LotIndex,
                    lotNumber: LotNumber,
                    myBid: BidderId === currentBidderId && BidderId !== -1,
                    paddleNumber: AssignedId,
                    source: BidSource,
                })
            );
        } else if (EventType === 'bid-retracted') {
            const { Amount, AskPrice, BidSource, BidderId, LotId, LotIndex, LotNumber } = event;
            return dispatch(
                bidRetracted({
                    amount: Amount,
                    ask: AskPrice,
                    bidderId: BidderId,
                    catalogId,
                    created,
                    currency,
                    itemId: LotId,
                    itemIndex: LotIndex,
                    lotNumber: LotNumber,
                    source: BidSource,
                })
            );
        } else if (EventType === 'lot-closed') {
            const { LotId, LotNumber } = event;
            return dispatch({
                payload: { catalogId, created, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_LOT_CLOSED,
            });
        } else if (EventType === 'lot-loaded') {
            const { NextLot } = event;
            return dispatch({
                payload: {
                    askPrice: NextLot.AskPrice,
                    catalogId,
                    created,
                    currency,
                    description: NextLot.LotDescription,
                    highEstimate: NextLot.HighEstimate,
                    index: NextLot.Index,
                    itemId: NextLot.LotId,
                    itemIndex: NextLot.Index,
                    lotNumber: NextLot.LotNumber,
                    lotTitle: NextLot.LotTitle,
                    lowEstimate: NextLot.LowEstimate,
                    status: NextLot.Status,
                },
                type: LIVE_NEXT_LOT_LOADED,
            });
        } else if (EventType === 'lot-passed') {
            const { LotId, LotNumber } = event;
            return dispatch({
                payload: { catalogId, created, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_LOT_PASSED,
            });
        } else if (EventType === 'lot-reopened') {
            const { LotId, LotNumber } = event;
            return dispatch({
                payload: { catalogId, created, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_LOT_REOPENED,
            });
        } else if (EventType === 'lot-skipped') {
            const { LotId, LotNumber } = event;
            return dispatch({
                payload: { catalogId, created, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_LOT_SKIPPED,
            });
        } else if (EventType === 'lot-sold') {
            const { Amount, AssignedId, BidderId, LotId, LotNumber } = event;
            return dispatch(
                lotSold({
                    amount: Amount,
                    bidderId: BidderId,
                    catalogId,
                    created,
                    currency,
                    itemId: LotId,
                    lotNumber: LotNumber,
                    myBid: BidderId === currentBidderId && BidderId !== -1,
                    paddleNumber: AssignedId,
                })
            );
        } else if (EventType === 'lot-unsold') {
            const { LotId, LotNumber } = event;
            return dispatch({
                payload: { catalogId, created, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_LOT_UNSOLD,
            });
        } else if (EventType === 'missive-sent') {
            const { LotId, MissiveText } = event;
            const { isTimed } = catalog;
            return dispatch({
                payload: {
                    catalogId,
                    created,
                    isTimed,
                    itemId: LotId,
                    missiveText: MissiveText,
                },
                type: LIVE_MISSIVE,
            });
        } else if (EventType === 'next-lot-opened') {
            const { Amount, LotId, LotNumber } = event;
            return dispatch({
                payload: { amount: Amount, catalogId, created, currency, itemId: LotId, lotNumber: LotNumber },
                type: LIVE_NEXT_LOT_OPENED,
            });
        }
    }
};

export const createPubnubCatalogOnPresence = (catalogId: number) => (presenceEvent: PubnubPresenceEvent) => async (
    dispatch: Function
) => {
    // console.log('presenceEvent', presenceEvent);
    if (presenceEvent.channel.includes(`${catalogId}`)) {
        if (presenceEvent.action === 'join') {
            return dispatch(
                updateActiveBiddersPubnub({
                    bidderCount: presenceEvent.occupancy,
                    catalogId,
                })
            );
        } else if (presenceEvent.action === 'leave') {
            return dispatch(
                updateActiveBiddersPubnub({
                    bidderCount: presenceEvent.occupancy,
                    catalogId,
                })
            );
        } else if (presenceEvent.action === 'timeout') {
            return dispatch(
                updateActiveBiddersPubnub({
                    bidderCount: presenceEvent.occupancy,
                    catalogId,
                })
            );
        } else if (presenceEvent.action === 'interval') {
            return dispatch(
                updateActiveBiddersPubnub({
                    bidderCount: presenceEvent.occupancy,
                    catalogId,
                })
            );
        }
    }
};

export const createPubnubCatalogOnStatus = (catalogId: number, channel: string) => (
    statusEvent: PubnubStatusEvent
) => async (dispatch: Function) => {
    // console.log('statusEvent', statusEvent);
    if (statusEvent.category === 'PNNetworkUpCategory') {
        return dispatch(networkReconnected({ catalogId }));
    } else if (statusEvent.category === 'PNNetworkDownCategory') {
        return dispatch(networkDisconnected({ catalogId }));
    } else if (statusEvent.category === 'PNNetworkIssuesCategory') {
        return dispatch(networkDisconnected({ catalogId }));
    } else if (statusEvent.category === 'PNReconnectedCategory') {
        return dispatch(networkReconnected({ catalogId }));
    } else if (statusEvent.category === 'PNConnectedCategory') {
        dispatch(networkReconnected({ catalogId }));
        return dispatch(hereNow(catalogId));
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNAccessDeniedCategory') {
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNMalformedResponseCategory') {
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNBadRequestCategory') {
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNDecryptionErrorCategory') {
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNUnknownCategory') {
        /* eslint-disable-next-line */
    } else if (statusEvent.category === 'PNTimeoutCategory') {
    }
    return Promise.resolve();
};

export const hereNow = (catalogId: number) => async (dispatch: Function) => {
    const channel = `catalog.${catalogId}`;
    const response = await pubnubService.hereNow(channel);
    return dispatch(
        updateActiveBiddersPubnub({
            bidderCount: response.channels[channel].occupancy,
            catalogId,
        })
    );
};

const bidAccepted = ({
    amount,
    ask,
    bidderId,
    catalogId,
    created,
    currency,
    itemId,
    itemIndex,
    lotNumber,
    myBid,
    paddleNumber,
    source,
}) => async (dispatch, getState) => {
    const state = getState();
    const currentBidderId = getBidderId(state);
    const { bidderHasHighBid, submitted } = getUiLiveBid(state);
    const bidderOutbid = (currentBidderId !== bidderId && bidderHasHighBid && bidderId !== -1) || (submitted && !myBid);
    const bidLimitBalance = getBidLimitBalance(state, catalogId, itemId);
    const { leadingBid: previousBidAmount } = getLiveItemData(state, itemId);

    if (myBid) {
        dispatch(fetchBidLimitBalanceIfNeeded(catalogId));
    }
    return dispatch({
        payload: {
            amount,
            ask,
            bidderId,
            bidderOutbid,
            bidLimitBalance,
            catalogId,
            created,
            currency,
            itemId,
            itemIndex,
            lotNumber,
            myBid,
            paddleNumber,
            previousBidAmount,
            source,
        },
        type: LIVE_BID_ACCEPTED,
    });
};

const bidRetracted = ({
    amount,
    ask,
    bidderId,
    catalogId,
    created,
    currency,
    itemId,
    itemIndex,
    lotNumber,
    source,
}) => async (dispatch, getState) => {
    const state = getState();
    const currentBidderId = getBidderId(state);
    const { leadingBidder } = getLiveItemData(state, itemId);
    const myBid = leadingBidder === currentBidderId;

    if (myBid) {
        dispatch(fetchBidLimitBalanceIfNeeded(catalogId));
    }
    return dispatch({
        payload: {
            amount,
            ask,
            bidderId,
            catalogId,
            created,
            currency,
            currentBidderId,
            itemId,
            itemIndex,
            lotNumber,
            myBid,
            source,
        },
        type: LIVE_BID_RETRACTED,
    });
};

const lotSold = ({
    amount,
    bidderId,
    catalogId,
    created,
    currency,
    itemId,
    lotNumber,
    myBid,
    paddleNumber,
}) => async dispatch => {
    if (myBid) {
        dispatch(fetchBidLimitBalanceIfNeeded(catalogId));
    }
    return dispatch({
        payload: { amount, bidderId, catalogId, created, currency, itemId, lotNumber, myBid, paddleNumber },
        type: LIVE_LOT_SOLD,
    });
};

const networkDisconnected = ({ catalogId }) => ({ payload: { catalogId }, type: LIVE_NETWORK_DISCONNECTED });
const networkReconnected = ({ catalogId }) => ({ payload: { catalogId }, type: LIVE_NETWORK_RECONNECTED });

const updateActiveBiddersPubnub = ({ bidderCount, catalogId }) => ({
    payload: { bidderCount, catalogId },
    type: LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
});
